import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Checkbox,
    IconButton,
    Link,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { formatDate } from 'utils/format';
import EventImageItem from 'components/shared/order-events-timeline/order-events/event-files/event-image-item.component';
import EventFileItem from 'components/shared/order-events-timeline/order-events/event-files/event-file-item.component';
import ImageGalleryDialog from 'components/shared/image-gallery-dialog/image-gallery-dialog.component';

export type SelectableFile = {
    fileId: string;
    filename: string;
    url: string;
    mimetype?: string | null;
    isScannerCaseFile: boolean;
    /** The scan case the file came from, shown instead of the uploader. */
    displayCaseId?: string;
    uploadedOn?: number | Date | null;
    uploadedBy?: string | null;
};

type SubcontractFilesTableProps = {
    files: SelectableFile[];
    isSelected: (file: SelectableFile) => boolean;
    onToggle: (file: SelectableFile) => void;
    onToggleAll: (select: boolean) => void;
};

const isImage = (file: SelectableFile) => /image/i.test(file.mimetype ?? '');

/**
 * DD-3485: the parent's files as on order details — preview, open and download — with a
 * checkbox each, so the lab can see what a file is before letting it travel.
 */
const SubcontractFilesTable: FC<SubcontractFilesTableProps> = ({ files, isSelected, onToggle, onToggleAll }) => {
    const { t: translate } = useTranslation(['common', 'organization']);
    const [galleryImage, setGalleryImage] = useState<string>();
    const imageUrls = files.filter(isImage).map((file) => file.url);
    const selectedCount = files.filter(isSelected).length;

    return (
        <Box mb={1}>
            <TableContainer>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell padding="checkbox">
                                <Checkbox
                                    checked={selectedCount === files.length}
                                    indeterminate={selectedCount > 0 && selectedCount < files.length}
                                    onChange={(_event, checked) => onToggleAll(checked)}
                                    inputProps={{ 'aria-label': translate('organization:SELECT_ALL') }}
                                    data-test="subcontract-files-table.select-all"
                                />
                            </TableCell>
                            <TableCell>{translate('common:FILE_IMAGE')}</TableCell>
                            <TableCell sx={{ wordBreak: 'break-all' }}>{translate('common:FILENAME')}</TableCell>
                            <TableCell sx={{ width: '200px' }}>{translate('common:UPLOADED_BY')}</TableCell>
                            <TableCell sx={{ width: '60px' }} />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {files.map((file) => (
                            <TableRow key={file.fileId} selected={isSelected(file)}>
                                <TableCell padding="checkbox">
                                    <Checkbox
                                        checked={isSelected(file)}
                                        onChange={() => onToggle(file)}
                                        inputProps={{ 'aria-label': file.filename }}
                                        data-test={`content-step.select-file-${file.fileId}`}
                                    />
                                </TableCell>
                                <TableCell sx={{ maxWidth: 150 }}>
                                    {isImage(file) ? (
                                        <Box onClick={() => setGalleryImage(file.url)}>
                                            <EventImageItem fileId={file.fileId} url={file.url} />
                                        </Box>
                                    ) : (
                                        <EventFileItem url={file.url} />
                                    )}
                                </TableCell>
                                <TableCell sx={{ wordBreak: 'break-all' }}>
                                    <Link href={file.url} target="_blank" rel="noreferrer" underline="none">
                                        {file.filename}
                                    </Link>
                                </TableCell>
                                <TableCell>
                                    {file.displayCaseId ? (
                                        <Typography variant="body2">{file.displayCaseId}</Typography>
                                    ) : (
                                        <>
                                            <Box>
                                                {file.uploadedOn ? formatDate(new Date(file.uploadedOn), 'date') : '-'}
                                            </Box>
                                            <Box>{file.uploadedBy || '-'}</Box>
                                        </>
                                    )}
                                </TableCell>
                                <TableCell align="right">
                                    <Tooltip title={translate('common:DOWNLOAD').toString()}>
                                        <IconButton
                                            color="primary"
                                            href={file.url}
                                            target="_blank"
                                            data-test={`subcontract-files-table.download-${file.fileId}`}
                                        >
                                            <FileDownloadIcon />
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <ImageGalleryDialog
                imageUrls={imageUrls}
                selectedImageUrl={galleryImage}
                open={Boolean(galleryImage)}
                onClose={() => setGalleryImage(undefined)}
            />
        </Box>
    );
};

export default SubcontractFilesTable;
